/**
 * HTTP client for LoginPortal API (fetch + AbortController).
 * Retry de red (VIP hairpin): solo Abort/TypeError/fetch fallido — no 401/409/423.
 */
import { isTigerEnvironment } from '../tigerDetector';

const DEFAULT_TIMEOUT_MS = 15000;
/** Lab VIP flaky: backoff login/biometric (no 401/409). */
const NETWORK_RETRY_DELAYS_MS = [500, 1500, 3000];
const NETWORK_RETRY_MAX_ATTEMPTS = 4;

function apiBase() {
  const base = process.env.REACT_APP_PORTAL_API_BASE || '/api/login-portal';
  return String(base).replace(/\/$/, '');
}

/**
 * @param {unknown} err
 * @returns {boolean}
 */
export function isTransientNetworkError(err) {
  if (err == null) return false;
  const status = err.status ?? err.statusCode;
  if (typeof status === 'number' && status >= 400) return false;
  const name = String(err.name || '');
  const msg = String(err.message || err.description || err || '');
  if (name === 'AbortError') return true;
  if (name === 'TypeError') return true;
  if (
    /Failed to fetch|NetworkError|network error|Load failed|ECONNRESET|ETIMEDOUT|timeout|aborted|ERR_CONNECTION/i.test(
      msg
    )
  ) {
    return true;
  }
  return false;
}

function sleepMs(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function fetchJsonOnce(path, { method = 'GET', body, timeoutMs = DEFAULT_TIMEOUT_MS, credentials } = {}) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const tiger = isTigerEnvironment();
    const response = await fetch(`${apiBase()}${path}`, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
      // Cookies Gateway: Tiger exchange, o logout (limpiar portal_session HttpOnly).
      credentials: credentials || (tiger ? 'include' : 'same-origin'),
    });
    let data = null;
    const text = await response.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { description: text };
      }
    }
    if (!response.ok) {
      const err = new Error(data?.message || data?.description || `HTTP ${response.status}`);
      err.status = response.status;
      err.payload = data;
      throw err;
    }
    return data;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * @param {string} path
 * @param {{ method?: string, body?: object, timeoutMs?: number, credentials?: RequestCredentials, retryNetwork?: boolean, maxAttempts?: number }} [options]
 */
async function fetchJson(path, options = {}) {
  const {
    retryNetwork = false,
    maxAttempts = NETWORK_RETRY_MAX_ATTEMPTS,
    ...onceOpts
  } = options;
  const attempts = retryNetwork ? Math.max(1, maxAttempts) : 1;
  let lastErr;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await fetchJsonOnce(path, onceOpts);
    } catch (err) {
      lastErr = err;
      const canRetry =
        retryNetwork &&
        attempt < attempts - 1 &&
        isTransientNetworkError(err);
      if (!canRetry) throw err;
      await sleepMs(NETWORK_RETRY_DELAYS_MS[attempt] ?? NETWORK_RETRY_DELAYS_MS[NETWORK_RETRY_DELAYS_MS.length - 1] ?? 800);
    }
  }
  throw lastErr;
}

/**
 * Cierra sesión Gateway (cookies portal_session + XSRF-TOKEN).
 */
export async function portalLogout() {
  return fetchJson('/v1/auth/logout', {
    method: 'POST',
    body: {},
    credentials: 'include',
  });
}

/**
 * Invalida cookies HttpOnly de sesión WMS del tenant (`token`, `refresh_token`).
 * Solo tiene sentido si Gateway y tenant comparten origen (lab legacy).
 * Multi-server: no-op — las cookies WMS viven en el DNS del cliente y se limpian allí.
 */
export async function clearWmsTenantSession() {
  // Gateway dedicado no puede Set-Cookie sobre el dominio del tenant.
  return false;
}

/**
 * Limpia sesión Gateway residual en la pantalla de login del portal.
 */
export async function clearResidualSessionsOnLoginScreen() {
  await Promise.allSettled([portalLogout()]);
}

/**
 * @param {string} [clienteCodigo] — obligatorio si el email está en varios clientes
 * @returns {Promise<{ redirect_to: string, cliente?: object, config?: object, channel?: string }>}
 */
export async function portalLogin(email, password, clienteCodigo) {
  const body = { email, password };
  if (clienteCodigo) body.cliente_codigo = clienteCodigo;
  // Solo mobile (Tiger). Web: sin flag → API redirige al DNS/portal-handoff del cliente.
  if (isTigerEnvironment()) body.client_channel = 'tiger';
  const envelope = await fetchJson('/v1/auth/login', {
    method: 'POST',
    body,
    retryNetwork: true,
  });
  const data = envelope?.data || envelope;
  if (!data?.redirect_to) {
    throw new Error('Respuesta de login incompleta');
  }
  return data;
}

/**
 * Branding público por código de cliente (sin sesión).
 * GET /v1/public-branding?cliente_codigo=
 * @returns {Promise<{ cliente: object, branding: object } | null>}
 */
export async function fetchPortalPublicBranding(clienteCodigo) {
  const codigo = String(clienteCodigo || '').trim();
  if (!codigo) return null;
  try {
    const envelope = await fetchJson(
      `/v1/public-branding?cliente_codigo=${encodeURIComponent(codigo)}`,
      { method: 'GET', timeoutMs: 5000 },
    );
    const data = envelope?.data || envelope;
    if (!data?.branding) return null;
    return data;
  } catch {
    return null;
  }
}

/**
 * Features públicas del portal (sin cliente_codigo).
 * @returns {Promise<{ features?: { biometric_webauthn?: boolean } }>}
 */
export async function fetchPortalPublicFeatures() {
  try {
    const envelope = await fetchJson('/v1/public-features', { method: 'GET', timeoutMs: 5000 });
    return envelope?.data || envelope || {};
  } catch {
    return { features: { biometric_webauthn: false } };
  }
}

function buildTigerLoginBody(email, extra = {}) {
  const body = { email, ...extra };
  if (isTigerEnvironment()) body.client_channel = 'tiger';
  return body;
}

/**
 * @param {string} email
 * @param {string} [clienteCodigo]
 */
export async function portalBiometricLoginOptions(email, clienteCodigo) {
  const body = buildTigerLoginBody(email);
  if (clienteCodigo) body.cliente_codigo = clienteCodigo;
  const envelope = await fetchJson('/v1/auth/biometric/login-options', {
    method: 'POST',
    body,
    retryNetwork: true,
  });
  return envelope?.data || envelope;
}

/**
 * Tiger WebAuthn: resolve cliente → URL de login en DNS tenant (auto_bio).
 * @param {string} email
 * @param {string} [clienteCodigo]
 * @returns {Promise<{ tenant_login_url: string, front_base_url?: string, cliente?: object }>}
 */
export async function portalBiometricRouteLogin(email, clienteCodigo) {
  const body = buildTigerLoginBody(email);
  if (clienteCodigo) body.cliente_codigo = clienteCodigo;
  const envelope = await fetchJson('/v1/auth/biometric/route-login', {
    method: 'POST',
    body,
    retryNetwork: true,
  });
  const data = envelope?.data || envelope;
  if (!data?.tenant_login_url) {
    throw new Error('Respuesta de ruta biométrica incompleta');
  }
  return data;
}

/**
 * @param {string} email
 * @param {object} assertionPayload
 * @param {string} [clienteCodigo]
 */
export async function portalBiometricVerifyLogin(email, assertionPayload, clienteCodigo) {
  const body = buildTigerLoginBody(email, assertionPayload);
  if (clienteCodigo) body.cliente_codigo = clienteCodigo;
  const envelope = await fetchJson('/v1/auth/biometric/verify-login', {
    method: 'POST',
    body,
    retryNetwork: true,
  });
  const data = envelope?.data || envelope;
  if (!data?.redirect_to) {
    throw new Error('Respuesta de login biométrico incompleta');
  }
  return data;
}
